import type { Container } from "@/types/container";

const SHIP_BOW_MARKER_SRC = `${import.meta.env.BASE_URL}images/ship-bow-marker.png`;

function getEtaLabel(eta?: Date, status?: string): string | null {
    if (!eta || status === "Arrived" || status === "Delivered") return null;
    const ms = eta.getTime() - Date.now();
    if (ms <= 0) return "ETA 0H";
    const totalHours = Math.round(ms / (1000 * 60 * 60));
    const days = Math.floor(totalHours / 24);
    const hours = totalHours % 24;
    return days > 0 ? `${days}D` : `${hours}H`;
}

function formatShortDate(date?: Date): string {
    if (!date) return "—";
    return date.toLocaleDateString(navigator.language, {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
}

export interface ContainerRouteProgressProps {
    container: Container;
    routeColor?: string;
    className?: string;
    showPortCodeBadges?: boolean;
    showCenterProgressBetweenPorts?: boolean;
    /** Optional normalized [0..1] backend progress source to keep UI aligned */
    progressOverride?: number;
    shipClampMinPct?: number;
    shipClampMaxPct?: number;
    clampPortNames?: boolean;
}

export function ContainerRouteProgress({
    container,
    routeColor = "#2563eb",
    className = "",
    showPortCodeBadges = true,
    showCenterProgressBetweenPorts = false,
    progressOverride,
    shipClampMinPct = 16,
    shipClampMaxPct = 84,
    clampPortNames = true,
}: ContainerRouteProgressProps) {
    // Draft: route completamente vuota
    if (container.status === "Draft") {
        return <div className={className} />;
    }

    const progressRatio = Math.max(
        0,
        Math.min(1, progressOverride ?? (container.progress ?? 0) / 100)
    );
    const progressPct = Math.round(progressRatio * 100);
    const fromCode = container.originPortCode || "—";
    const toCode = container.destinationPortCode || "—";
    const etaLabel = getEtaLabel(container.eta, container.status);
    const departureDate = container.actualDepartureDate ?? container.estimatedDepartureDate;
    // Keep ship marker between left/right port badges.
    const shipPositionPct = Math.max(shipClampMinPct, Math.min(shipClampMaxPct, progressRatio * 100));
    const waypoints = container.waypoints ?? [];
    const stopsCount = Math.max(0, waypoints.length - 2);
    const stopPoints =
        stopsCount > 0
            ? Array.from({ length: stopsCount }, (_, i) => ((i + 1) / (stopsCount + 1)) * 100).filter(
                  (pct) => pct <= progressRatio * 100
              )
            : [];

    return (
        <div className={`space-y-1.5 ${className}`}>
            <div className="relative isolate h-5">
                <div
                    className="pointer-events-none absolute left-0 top-1/2 z-0 box-border h-0 -translate-y-1/2 border-t-2 border-dashed border-tertiary"
                    style={{ width: `${progressRatio * 100}%` }}
                />
                <div
                    className="pointer-events-none absolute top-1/2 z-0 box-border h-0 -translate-y-1/2"
                    style={{
                        left: `${progressRatio * 100}%`,
                        width: `${100 - progressRatio * 100}%`,
                        borderTopWidth: 2,
                        borderTopStyle: "solid",
                        borderTopColor: routeColor,
                    }}
                />
                {stopPoints.map((pct, idx) => (
                    <span
                        key={`${container.id}-stop-${idx}`}
                        className="pointer-events-none absolute top-1/2 z-0 size-1 -translate-x-1/2 -translate-y-1/2 rounded-full bg-tertiary"
                        style={{ left: `${pct}%` }}
                    />
                ))}
                {showPortCodeBadges && (
                    <>
                        <span className="absolute left-0 top-1/2 z-10 -translate-y-1/2 rounded bg-secondary px-1.5 py-0.5 text-[10px] font-medium text-tertiary ring-1 ring-secondary_alt">
                            {fromCode}
                        </span>
                        <span className="absolute right-0 top-1/2 z-10 -translate-y-1/2 rounded bg-secondary px-1.5 py-0.5 text-[10px] font-medium text-tertiary ring-1 ring-secondary_alt">
                            {toCode}
                        </span>
                    </>
                )}
                {etaLabel && (
                    <span
                        className="absolute top-1/2 z-10 -translate-y-1/2 rounded bg-secondary px-1.5 py-0.5 text-[10px] font-medium text-tertiary ring-1 ring-secondary_alt"
                        style={{ left: `${Math.min(82, Math.max(50, progressRatio * 100 + 16))}%` }}
                    >
                        {etaLabel}
                    </span>
                )}
                <span
                    className="absolute top-1/2 z-20 flex size-[22px] -translate-x-1/2 -translate-y-1/2 items-center justify-center"
                    style={{ left: `${shipPositionPct}%` }}
                    aria-hidden="true"
                >
                    <img
                        src={SHIP_BOW_MARKER_SRC}
                        alt=""
                        width={22}
                        height={22}
                        draggable={false}
                        className="pointer-events-none select-none object-contain drop-shadow-sm"
                        style={{
                            filter: "invert(1) brightness(1.08) drop-shadow(0 0 1px rgba(15,23,42,0.9))",
                        }}
                    />
                </span>
            </div>

            <div className={`grid ${showCenterProgressBetweenPorts ? "grid-cols-3" : "grid-cols-2"} gap-2 text-xs`}>
                <div className="min-w-0">
                    <div className={clampPortNames ? "line-clamp-2 text-tertiary" : "text-tertiary break-words"}>
                        {container.route.from}
                    </div>
                    <div className="text-quaternary">{formatShortDate(departureDate)}</div>
                </div>
                {showCenterProgressBetweenPorts && (
                    <div className="flex flex-col items-center justify-center text-center">
                        <div className="text-[11px] font-semibold text-primary">{progressPct}%</div>
                    </div>
                )}
                <div className="min-w-0 text-right">
                    <div className={clampPortNames ? "line-clamp-2 text-tertiary" : "text-tertiary break-words"}>
                        {container.route.to}
                    </div>
                    <div className="text-quaternary">{formatShortDate(container.eta)}</div>
                </div>
            </div>
        </div>
    );
}
